import { Check, Plus } from 'lucide-react';
import { LazyVideo } from './lazy-video';

/**
 * Body sections of a generator page. All content arrives via props; each
 * renders inside a ToolSection the block supplies.
 */

export function ProseParagraphs({ paragraphs }: { paragraphs: readonly string[] }) {
  return (
    <div className="space-y-5 text-pretty text-base leading-8 text-muted-foreground sm:text-[1.0625rem]">
      {paragraphs.map((paragraph, index) => (
        <p key={index} className={index === 0 ? 'text-foreground' : undefined}>
          {paragraph}
        </p>
      ))}
    </div>
  );
}

/** Label/value facts in a quiet card ("At a glance"). */
export function FactList({ title, items }: { title: string; items: readonly { label: string; value: string }[] }) {
  return (
    <aside className="rounded-2xl bg-muted/45 p-6 ring-1 ring-border/60 sm:p-8 dark:bg-white/[0.04] dark:ring-white/10">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{title}</p>
      <dl className="mt-5 divide-y divide-border/70">
        {items.map((item) => (
          <div key={item.label} className="grid grid-cols-[7rem_1fr] gap-4 py-3.5 text-sm first:pt-0 last:pb-0">
            <dt className="text-muted-foreground">{item.label}</dt>
            <dd className="font-medium text-foreground">{item.value}</dd>
          </div>
        ))}
      </dl>
    </aside>
  );
}

/** Steps as a timeline: big numerals joined by a rule on wide screens. */
export function NumberedSteps({ steps }: { steps: readonly { title: string; body: string }[] }) {
  return (
    <div className="relative">
      <div className="absolute left-0 right-0 top-6 hidden h-px bg-border sm:block" aria-hidden="true" />
      <ol className="relative grid gap-10 sm:grid-cols-3 sm:gap-8">
      {steps.map((step, index) => (
        <li key={index} className="relative">
          <span className="relative flex size-12 items-center justify-center rounded-full bg-background text-lg font-bold text-primary ring-1 ring-border">
            {String(index + 1).padStart(2, '0')}
          </span>
          <h3 className="mt-5 text-lg font-semibold tracking-tight">{step.title}</h3>
          <p className="mt-2 text-sm leading-7 text-muted-foreground">{step.body}</p>
        </li>
      ))}
      </ol>
    </div>
  );
}

/** Gallery cards: the swatch carries the title, the description sits below. */
export function SwatchCardGrid({
  items,
}: {
  items: readonly { id: string; title: string; body: string; swatch: string; image?: string; video?: string }[];
}) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-6 lg:grid-cols-4">
      {items.map((item) => (
        <figure key={item.id} className="group">
          <div
            className="relative flex aspect-[4/5] items-end overflow-hidden rounded-2xl p-4 shadow-sm sm:p-5 ring-1 ring-black/5 transition-transform duration-300 group-hover:-translate-y-1"
            style={{ background: item.swatch }}
          >
            {item.video ? (
              <LazyVideo src={item.video} poster={item.image} label={item.title} className="absolute inset-0 size-full" />
            ) : item.image ? (
              <img
                src={item.image}
                alt=""
                loading="lazy"
                className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0" aria-hidden="true" />
            <h3 className="relative text-base font-bold tracking-tight text-white sm:text-lg">{item.title}</h3>
          </div>
          <figcaption className="mt-3 text-sm leading-6 text-muted-foreground sm:mt-4 sm:leading-7">{item.body}</figcaption>
        </figure>
      ))}
    </div>
  );
}

/** Feature tiles: emoji badge, title, copy — no borders, generous gaps. */
export function EmojiCardGrid({ items }: { items: readonly { emoji: string; title: string; body: string }[] }) {
  return (
    <div className="grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((item, index) => (
        <div key={index}>
          <span
            className="flex size-12 items-center justify-center rounded-2xl bg-background text-2xl shadow-sm ring-1 ring-border"
            aria-hidden="true"
          >
            {item.emoji}
          </span>
          <h3 className="mt-5 text-base font-semibold tracking-tight">{item.title}</h3>
          <p className="mt-2 text-sm leading-7 text-muted-foreground">{item.body}</p>
        </div>
      ))}
    </div>
  );
}

export function Checklist({ items }: { items: readonly string[] }) {
  return (
    <ul className="grid gap-x-10 gap-y-5 sm:grid-cols-2">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-3 text-base leading-7">
          <span className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Check className="size-3.5" aria-hidden="true" />
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function CostTable({
  caption,
  headers,
  rows,
}: {
  caption: string;
  headers: { label: string; value: string };
  rows: readonly { id: string; label: string; value: string | number }[];
}) {
  return (
    <div className="mx-auto max-w-2xl">
      <table className="w-full text-sm">
        <caption className="pb-3 text-left text-sm font-semibold text-foreground">{caption}</caption>
        <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2.5 font-medium">{headers.label}</th>
            <th className="py-2.5 text-right font-medium">{headers.value}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-border/70 last:border-0">
              <td className="py-3.5">{row.label}</td>
              <td className="py-3.5 text-right font-semibold tabular-nums">{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Native <details> FAQ: answers stay in the HTML for crawlers; the first one starts open. */
export function FaqList({ items }: { items: readonly { q: string; a: string }[] }) {
  return (
    <div className="divide-y divide-border border-y border-border">
      {items.map((item, index) => (
        <details key={index} className="group py-5" open={index === 0}>
          <summary className="flex cursor-pointer list-none items-start justify-between gap-6 [&::-webkit-details-marker]:hidden">
            <h3 className="text-base font-semibold leading-7 text-foreground">{item.q}</h3>
            <Plus
              className="mt-1 size-5 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-45"
              aria-hidden="true"
            />
          </summary>
          <p className="mt-3 max-w-2xl pr-10 text-base leading-7 text-muted-foreground">{item.a}</p>
        </details>
      ))}
    </div>
  );
}
